import { useEffect, useState } from "react";

/**
 * Reactive viewport-width hook. Returns `true` while the window is
 * narrower than the Tailwind `md` breakpoint (768px). Used to switch
 * the admin/staff layout into a card-based, view-only mobile shell
 * and to hide any editor UI staff would normally see on desktop.
 *
 * SSR-safe: falls back to `false` when `window` is undefined so the
 * initial render on the server (or during hydration) matches the
 * desktop layout, then swaps on mount.
 */
export function useIsMobile(breakpointPx = 768) {
  const query = `(max-width: ${breakpointPx - 1}px)`;
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    // Chrome/Safari use addEventListener, older browsers use addListener.
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    // Sync once in case the initial state differed (e.g. SSR default).
    setIsMobile(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, [query]);

  return isMobile;
}
